import { useEffect, useCallback } from "react";
import { usePartialState } from "../../helper/Hooks/usePartialState";
import { Clinic, getClinics } from "../../services/getFunctions";
import { LoginConfig } from "./loginConfigs";
import { useAlertHelpers } from "../../helper/Hooks/useAlertHelpers";
import { toErrorType } from "../../helper/alertBoxes";
import { FormControlElement } from "../../types/uiTypes";

/**
 * General Login status states
 */
export interface LoginState {
  clinicId: string;
  clinicname: string;
  clinic_contact: string;
  clinics: Clinic[];
  username: string;
  password: string;
}

/**
 * Custom Hook return value
 */
export interface UseLoginStateResult extends LoginState {
  handleUsername: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handlePassword: (event: React.ChangeEvent<FormControlElement>) => void;
  handleClinicId: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  updateState: (updates: Partial<LoginState>) => void;
}

/**
 * Custom Hook: Unified management of common login logic
 *
 * - Manage general form states(username, password, clinicId)
 * - Load the clinic list
 * - Provide handlers for form input changes
 */
export const useLoginState = (config: LoginConfig): UseLoginStateResult => {
  const { reportGetClinicListError } = useAlertHelpers();

  const initialState: LoginState = {
    clinicId: "",
    clinicname: "",
    clinic_contact: "",
    clinics: [],
    username: "",
    password: "",
  };

  const [state, updateState] = usePartialState(initialState);

  //load clinic list
  useEffect(() => {
    const loadClinics = async () => {
      try {
        const clinicList = await getClinics();
        console.log("Klinik-Liste: ", clinicList);
        updateState({ clinics: clinicList.clinics });
      } catch (error) {
        reportGetClinicListError(toErrorType(error));
      }
    };
    loadClinics();
  }, [updateState, reportGetClinicListError]);

  /**Method to update the clinic id state, when it is changed.
   * @param event {ChangeEvent} onChange delivers the event with the selected option
   */
  const handleClinicId = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const clinicId = event.target.value;
      const clinic = state.clinics.find((c) => c.id === clinicId);

      if (!clinic) {
        updateState({ clinicId: clinicId, clinicname: "", clinic_contact: "" });
        return;
      }

      updateState({
        clinicId: clinicId,
        clinicname: clinic.name,
        clinic_contact: config.userType === "patient" ? clinic.contact : "",
      });
      console.log("Klinik-ID: ", clinicId);
      console.log("Klinik-Event: ", event);
    },
    [state.clinics, updateState, config.userType],
  );

  /**
   * Method to update the username state, when it is changed.
   */
  const handleUsername = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newUsername = event.target.value;
      updateState({ username: newUsername });
    },
    [updateState],
  );

  /**
   * Method to update the password state, when it is changed.
   */
  const handlePassword = useCallback(
    (event: React.ChangeEvent<FormControlElement>) => {
      const newPassword = event.target.value;
      updateState({ password: newPassword });
    },
    [updateState],
  );

  return {
    ...state,
    handleClinicId,
    handlePassword,
    handleUsername,
    updateState,
  };
};
